import { inject } from '@angular/core';
import type { CanActivateFn, UrlTree } from '@angular/router';
import { Router } from '@angular/router';

import { SessionService } from '@rpgc-ai/services/auth/session.service';
import { LanguageService } from '@rpgc-ai/services/translate/language.service';

import { AuthStore } from '@rpgc-ai/store/auth/auth.store';
import { ProfileStore } from '@rpgc-ai/store/profile/profile.store';

/**
 * Guard для защиты маршрутов, требующих авторизации
 * Если пользователь не авторизован, перенаправляет на страницу входа
 */
export const authGuard: CanActivateFn = async (route, state): Promise<boolean | UrlTree> => {
  const authStore = inject(AuthStore);
  const sessionService = inject(SessionService);
  const router = inject(Router);
  // 1) Уже авторизован в сторе
  if (authStore.isAuthenticated()) {
    return true;
  }

  // 2) Статус из SessionService уже известен (например, из TransferState)
  const ssrAuth = sessionService.isAuthenticated();
  if (ssrAuth === true) {
    return true;
  }

  // 3) Если статус ещё не определён, пробуем один раз загрузить сессию (SSR использует REQUEST headers)
  if (ssrAuth === undefined) {
    try {
      const data = await sessionService.getSession();
      if (data) {
        return true;
      }
    } catch {
      // ignore errors, fallback to redirect
    }
  }

  // 4) Не авторизован — редиректим на корень
  return router.createUrlTree(['/', { outlets: { modal: ['sign-in'] } }]);
};

/**
 * Инвертированный guard для защиты маршрутов, требующих авторизации
 */
export const authGuardInverted: CanActivateFn = async (route, state): Promise<boolean | UrlTree> => {
  const router = inject(Router);
  const sessionService = inject(SessionService);
  const languageService = inject(LanguageService);

  // Статус из SessionService уже известен
  const ssrAuth = sessionService.isAuthenticated();

  if (ssrAuth === false) {
    return true; // Не авторизован - пропускаем
  }

  // Если статус ещё не определён, пробуем загрузить сессию
  if (ssrAuth === undefined) {
    try {
      const data = await sessionService.getSession();
      if (!data) {
        return true; // Сессия не найдена - пропускаем
      }
    } catch {
      return true; // При ошибке считаем неавторизованным
    }
  }

  // Пользователь авторизован - нужен редирект
  const currentUrl = state.url;
  const langMatch = currentUrl.match(/^\/([a-z]{2})(\/|$)/);
  const lang = langMatch ? langMatch[1] : languageService.getCurrentLanguage() || 'en';

  // Для модальных роутов - закрываем модалку и редиректим на главную одной командой
  if (route.outlet === 'modal') {
    return router.createUrlTree([lang], {
      queryParams: route.queryParams,
      queryParamsHandling: 'preserve',
    });
  }

  // Для обычных роутов
  return router.createUrlTree([lang]);
};

export const tosGuard: CanActivateFn = (): boolean | UrlTree => {
  const store = inject(ProfileStore);
  const router = inject(Router);

  const { isAdultConfirmed, isTermsAccepted, id } = store.profile() ?? {};

  if (!!id && (!isAdultConfirmed || !isTermsAccepted)) {
    return router.createUrlTree(['/', { outlets: { modal: ['welcome'] } }]);
  }
  return true;
};
